import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { push } from 'redux-router';
import moment from 'moment';
import has from 'lodash/has';
import forEach from 'lodash/forEach';
import debounce from 'lodash/debounce';
import { updateEvent, addEvent, setFlashMessage, removeFlashMessage, checkEventConflicts, dataGroupResetAll } from 'actions';
import Calendar from 'lib/Calendar';

import Loading from 'components/common/Loading';
import CalendarListSection from 'components/calendar/CalendarListSection';
import DatePicker from 'components/calendar/DatePicker';
import Textarea from 'components/common/Textarea';
import EventDataGroup from 'components/calendar/EventDataGroup';
import OverrideConflicts from 'components/inbox/OverrideConflicts';
import EventBubble from 'components/calendar/EventBubble';

import Input from '@klarna/ui/uncontrolled/Input';
import * as UncontrolledSwitch from '@klarna/ui/uncontrolled/Switch';

const STYLE = {
  toggle: {
    display: 'inline-block',
    minWidth: 'auto',
    paddingRight: 10,
  },
  inlineToggle: {
    position: 'absolute',
    top: 15,
    right: 15,
    zIndex: 99,
  },
};

class EditEventDetails extends Component {
  static formatTime(time, fallback = '12:00', onFallBack = () => {}) {
    const timeFormat = Calendar.getTimeMomentFormat();
    const timeMoment = moment(time, timeFormat);
    const fallbackMoment = moment(fallback, ['HH:mm', 'h:mm a']);
    if (!timeMoment.isValid()) {
      setTimeout(() => {
        onFallBack(fallbackMoment.format('HH:mm'));
      });
      return fallbackMoment.format('HH:mm');
    }
    return timeMoment.format('HH:mm');
  }

  static makeSingular(title) {
    let end = title.substr((title.length - 3));

    if (end === 'ies') {
      end = 'y';
    } else if (end === 'ses') {
      end = end.charAt(0);
    } else if (end.charAt(2) === 's') {
      end = end.charAt(0) + end.charAt(1);
    }

    return title.substr(0, (title.length - 3)) + end;
  }

  static searchBrowseFromConfig(config = null) {
    let searchBrowseSections = {};

    searchBrowseSections.locations = Calendar.formatSearchBrowse(
      'locations',
      'school_local_facility,district_school,local_facility,school_remote_facility,non_district_school,remote_facility',
      'Locations'
    );
    searchBrowseSections.locations.additional_fields = 'local_facility:school,remote_facility:schools';
    searchBrowseSections.locations.subsections_hierarchy = ['local_facility', 'remote_facility'];
    searchBrowseSections.locations.subsections_hidden = ['district_school', 'local_facility', 'non_district_school', 'remote_facility'];
    // searchBrowseSections.locations.subsections_search = ['school_local_facility', 'school_remote_facility'];
    searchBrowseSections.locations.subsections_sub_object = ['school_local_facility', 'district_school', 'school_remote_facility', 'non_district_school'];
    searchBrowseSections.locations.subsection_primary = 'school_local_facility';

    searchBrowseSections.groups = Calendar.formatSearchBrowse(
      'groups',
      'school_group_school_restricted,district_school,school_group_restricted,community_group_school_restricted,non_district_school,community_group_restricted',
      'Participants'
    );
    searchBrowseSections.groups.subsections_hidden = ['district_school', 'school_group_restricted', 'non_district_school', 'community_group_restricted'];
    // searchBrowseSections.groups.subsections_search = ['school_group_school_restricted', 'community_group_school_restricted'];
    searchBrowseSections.groups.subsections_sub_object = ['school_group_school_restricted', 'district_school', 'community_group_school_restricted', 'non_district_school'];
    searchBrowseSections.groups.subsection_primary = 'school_group_school_restricted';
    searchBrowseSections.groups.subsections_disabled = [];
    searchBrowseSections.groups.additional_fields = 'school_group_restricted:schools,community_group_restricted:schools';

    searchBrowseSections.departments = Calendar.formatSearchBrowse('departments', 'department', 'Departments');

    searchBrowseSections.activities = Calendar.formatSearchBrowse('activities', 'activity', 'Activities');

    searchBrowseSections.event_types = Calendar.formatSearchBrowse('event_types', 'event_type', 'Event Types');
    searchBrowseSections.event_types.single_selection = true;

    searchBrowseSections.equipment = Calendar.formatSearchBrowse('equipment', 'equipment', 'Equipment');
    searchBrowseSections.equipment.additional_fields = 'equipment:quantity';
    searchBrowseSections.equipment.subsection_primary = 'equipment';
    searchBrowseSections.equipment.subsections_disabled = [];
    searchBrowseSections.equipment.subsections_disabled_always_show = [];

    if (config) {
      const {
        enable_schools: enableSchools,
        event_disable_school_locations: disableSchoolLocations,
        event_wizard_enable_all_school_search: enableAllSchoolSearch,
        event_wizard_enable_google_place_search: enableGooglePlaceSearch,
        enable_event_user_participants: enableEventUserParticipants,
        calendar_type: calendarType,
        calendar_sub_type: calendarSubType,
      } = config;

      let calendarIsSchool = false;

      if (calendarType === 6 || calendarType === 7 || (calendarType === 1 && (calendarSubType === 1 || calendarSubType === 2))) {
        calendarIsSchool = true;
      }

      if (enableSchools === '1') {
        if (!calendarIsSchool) {
          searchBrowseSections.locations.subsections = ['local_facility', 'school_remote_facility', 'non_district_school', 'remote_facility'];
          searchBrowseSections.locations.additional_fields = 'remote_facility:schools';
          searchBrowseSections.locations.subsections_hidden = ['non_district_school', 'remote_facility'];
          // searchBrowseSections.locations.subsections_search = ['local_facility', 'school_remote_facility'];
          searchBrowseSections.locations.subsections_sub_object = ['school_remote_facility', 'non_district_school'];
          searchBrowseSections.locations.subsection_primary = 'local_facility';

          searchBrowseSections.groups.subsections = ['school_group_restricted', 'community_group_school_restricted', 'non_district_school', 'community_group_restricted'];
          searchBrowseSections.groups.subsections_hidden = ['non_district_school', 'community_group_restricted'];
          // searchBrowseSections.groups.subsections_search = ['school_group_restricted', 'community_group_school_restricted'];
          searchBrowseSections.groups.subsections_sub_object = ['community_group_school_restricted', 'non_district_school'];
          searchBrowseSections.groups.additional_fields = 'community_group_restricted:schools';
          searchBrowseSections.groups.subsection_primary = 'school_group_restricted';
        } else if (disableSchoolLocations === '1') {
          searchBrowseSections.locations.subsections = ['local_facility', 'school_remote_facility', 'non_district_school', 'remote_facility'];
          searchBrowseSections.locations.subsections_hidden = ['district_school', 'non_district_school', 'remote_facility'];
          // searchBrowseSections.locations.subsections_search = ['local_facility', 'school_remote_facility'];
          searchBrowseSections.locations.subsections_sub_object = ['district_school', 'school_remote_facility', 'non_district_school'];
          searchBrowseSections.locations.subsection_primary = 'local_facility';
        }

        if(enableEventUserParticipants == 1) {
          if(calendarIsSchool) {
            searchBrowseSections.groups.subsections.push('school_user', 'user');
            searchBrowseSections.groups.subsections_hidden.push('user');
            // searchBrowseSections.groups.subsections_search.push('school_user');
            searchBrowseSections.groups.subsections_sub_object.push('school_user');
            searchBrowseSections.groups.subsections_disabled.push('school_user');

            if(searchBrowseSections.groups.additional_fields.length > 0) searchBrowseSections.groups.additional_fields += ',';
            searchBrowseSections.groups.additional_fields += 'user:school';
          } else {
            searchBrowseSections.groups.subsections.push('user');
            // searchBrowseSections.groups.subsections_search.push('user');
          }
        }

        if (enableAllSchoolSearch === '1') {
          searchBrowseSections.locations.subsections.push('directory_school');
          searchBrowseSections.locations.subsections_hidden.push('directory_school');
          // searchBrowseSections.locations.subsections_search.push('directory_school');

          searchBrowseSections.groups.subsections.push('directory_school');
          searchBrowseSections.groups.subsections_hidden.push('directory_school');
          // searchBrowseSections.groups.subsections_search.push('directory_school');
        }

        if (calendarIsSchool) {
          searchBrowseSections.equipment.subsections = ['school_equipment', 'equipment'];
          searchBrowseSections.equipment.subsections_hidden = ['equipment'];
          // searchBrowseSections.equipment.subsections_search = ['school_equipment'];
          searchBrowseSections.equipment.subsections_sub_object = ['school_equipment'];
          searchBrowseSections.equipment.additional_fields = 'equipment:quantity,equipment:school';
          searchBrowseSections.equipment.use_id_prefixes = false;
          searchBrowseSections.equipment.add_subsection_title = false;
          searchBrowseSections.equipment.subsections_disabled.push('school_equipment');
          searchBrowseSections.equipment.subsections_disabled_always_show.push('school_equipment');
          searchBrowseSections.equipment.subsection_primary = 'school_equipment';
        }
      } else {
        searchBrowseSections.locations.subsections = ['local_facility', 'remote_facility'];
        searchBrowseSections.locations.subsections_hidden = [];
        // searchBrowseSections.locations.subsections_search = ['local_facility', 'remote_facility'];
        searchBrowseSections.locations.subsections_sub_object = [];
        searchBrowseSections.locations.additional_fields = '';
        searchBrowseSections.locations.subsection_primary = 'local_facility';

        searchBrowseSections.groups.subsections = ['school_group_restricted', 'community_group_restricted'];
        searchBrowseSections.groups.subsections_hidden = [];
        // searchBrowseSections.groups.subsections_search = ['school_group_restricted', 'community_group_restricted'];
        searchBrowseSections.groups.subsections_sub_object = [];
        searchBrowseSections.groups.additional_fields = '';
        searchBrowseSections.groups.subsection_primary = 'school_group_restricted';

        if(enableEventUserParticipants == 1) {
          searchBrowseSections.groups.subsections.push('user');
          // searchBrowseSections.groups.subsections_search.push('user');
        }

      }

      if (enableGooglePlaceSearch === '1') {
        searchBrowseSections.locations.subsections.push('google_place');
        searchBrowseSections.locations.subsections_hidden.push('google_place');
        // searchBrowseSections.locations.subsections_search.push('google_place');
      }
    }

    return searchBrowseSections;
  }

  constructor(props) {
    super(props);

    this.props.dataGroupResetAll();

    this.state = {
      event: this.props.event || {},
    };

    const { event } = this.state;

    if (event.time_setup || event.time_breakdown) {
      event.has_setup_breakdown = true;
    }

    if( event.config.simple_privacy !== '1' ) {
      if (event.user_viewing_groups) {
        if (typeof event.user_viewing_groups === 'string') {
          event.user_viewing_groups = event.user_viewing_groups.split(',');
        } else if (typeof event.user_viewing_groups === 'number') {
          event.user_viewing_groups = new Array( event.user_viewing_groups );
        } else if (typeof event.user_viewing_groups === 'object') {
          event.user_viewing_groups = event.user_viewing_groups.map(q => q.id);
        } else {
          event.user_viewing_groups = new Array();
        }

        if(event.user_viewing_groups.length > 0) {
          event.private = true;
        } else {
          event.private = false;
        }
      } else {
        event.user_viewing_groups = new Array();
        event.private = false;
      }
    } else if (typeof event.private == 'string' || typeof event.private == 'number') {
      if(event.private === 't' || event.private === '1' || event.private === 1) {
        event.private = true;
      } else {
        event.private = false;
      }
    } else if (typeof event.private == 'undefined') {
      event.private = false;
    }

    if (event.display_groups) {
      event.display_groups.forEach((group) => {
        const prefixedId = `${Calendar.determineIdPrefix(group.subsection)}${group.id}`;
        if (has(group, 'other.home_away_type')) {
          event[`home_away_neutral_${prefixedId}`] = group.other.home_away_type;
        }
        if (has(group, 'other.early_dismissal')) {
          event[`time_dismiss_${prefixedId}`] = group.other.early_dismissal;
        }
      });
    }

    if (event.display_equipment && event.config.enable_equipment_quantities === true) {
      event.display_equipment.forEach((equipment) => {
        if (has(equipment, 'other.quantity_used')) {
          event[`equipment_quantity_${equipment.id}`] = equipment.other.quantity_used;
        }
      });
    }

    this.debouncedConflictCheck = debounce(this.props.checkEventConflicts, 500);

    this.searchBrowseSections = EditEventDetails.searchBrowseFromConfig(event.config);

    let showOverrideConflictsCheckbox = true;
    let groupConflictHide = false;
    let facilityConflictHide = false;
    let equipmentConflictHide = false;
    let enableEquipmentQuantities = false;
    let enableEventVirtualMeetingLink = false;

    if (event.config) {
      if (event.config.request === '1') {

        if (!event.config.is_approval_admin && event.config.deny_event_requests_with_conflicts === '1') {
          showOverrideConflictsCheckbox = false;
        }

        if (event.config.group_conflict_hide === '1') {
          groupConflictHide = true;
        }

        if (event.config.facility_conflict_hide === '1') {
          facilityConflictHide = true;
        }

        if (event.config.equipment_conflict_hide === '1') {
          equipmentConflictHide = true;
        }
      } else {
        if (event.config.event_group_conflict_hide === '1') {
          groupConflictHide = true;
        }

        if (event.config.event_facility_conflict_hide === '1') {
          facilityConflictHide = true;
        }

        if (event.config.event_equipment_conflict_hide === '1') {
          equipmentConflictHide = true;
        }
      }

      enableEquipmentQuantities = event.config.enable_equipment_quantities;
    }

    if (window.CONFIG.enable_event_virtual_meeting_link) {
      if (window.CONFIG.enable_event_virtual_meeting_link === '1') {
        enableEventVirtualMeetingLink = true;
      }
    }

    this.showOverrideConflictsCheckbox = showOverrideConflictsCheckbox;
    this.groupConflictHide = groupConflictHide;
    this.facilityConflictHide = facilityConflictHide;
    this.equipmentConflictHide = equipmentConflictHide;
    this.enableEquipmentQuantities = enableEquipmentQuantities;
    this.enableEventVirtualMeetingLink = enableEventVirtualMeetingLink;

    this.locationsEventDataGroup = null;
    this.groupsEventDataGroup = null;

    this.setLocationsEventDataGroupRef = element => {
      this.locationsEventDataGroup = element;
    };

    this.setGroupsEventDataGroupRef = element => {
      this.groupsEventDataGroup = element;
    };
  }

  componentWillReceiveProps(next) {
    if (next.event && (!this.state.event.config || this.state.event.id !== next.event.id)) {
      this.setState({ event: next.event });
      this.props.dataGroupResetAll();
    }
  }

  onStartDateChange = (newDate) => {
    const formatted = newDate.format('YYYY-MM-DD');

    const timeFormat = Calendar.getTimeMomentFormat();

    this.setState((prevState) => {
      const event = { ...prevState.event };
      const labels = {
        ...event.labels,
        YYYMMDD: formatted,
        start_moment: moment(`${formatted} ${(event.time_start || '00:00')}`, 'YYYY-MM-DD ' + timeFormat),
        end_moment: moment(`${(event.date_end || formatted)} ${(event.time_end || '11:59 pm')}`, 'YYYY-MM-DD ' + timeFormat),
      };
      return { event: { ...event, date_start: formatted, labels } };
    }, () => this.doConflictCheck());
  }

  onEndDateChange = (newDate) => {
    const formatted = newDate.format('YYYY-MM-DD');

    const timeFormat = Calendar.getTimeMomentFormat();

    this.setState((prevState) => {
      const event = { ...prevState.event };
      const labels = {
        ...event.labels,
        end_moment: moment(`${(formatted)} ${(event.time_end || '23:59')}`, 'YYYY-MM-DD ' + timeFormat),
      };
      return { event: { ...event, date_end: formatted, labels } };
    }, () => this.doConflictCheck());
  }

  getSubmitButton = () => {
    let buttonText = 'Save Event';
    let onClick;

    if (this.props.event.id) {
      onClick = () => this.props.updateEvent(this.state.event, () => {
        this.props.setFlashMessage('Event Updated', 'success');
        setTimeout(() => {
          this.props.push(`/calendar/details/${this.state.event.id}`);
        }, 1500);
        setTimeout(() => {
          this.props.removeFlashMessage();
        }, 5000);
      }, (error) => {
        this.props.setFlashMessage((
          <span>
            Error Updating Event
            {error.error_msgs.map((e, i) => (<span key={i}><br /><span>- {e}</span></span>))}
          </span>
        ), 'error');
      });
    } else {
      buttonText = 'Add Event';
      onClick = () => this.props.addEvent(this.state.event, (response) => {
        this.props.setFlashMessage('Event Added', 'success');
        setTimeout(() => {
          this.props.push(`/calendar/details/${response.id}`);
        }, 1500);
        setTimeout(() => {
          this.props.removeFlashMessage();
        }, 5000);
      }, (error) => {
        this.props.setFlashMessage((
          <span>
            Error Adding Event
            {error.error_msgs.map((e, i) => (<span key={i}><br /><span>- {e}</span></span>))}
          </span>
        ), 'error');
      });
    }

    return (
      <button
        className="block accept"
        style={{ marginTop: 20 }}
        onClick={onClick}
      >{buttonText}</button>
    );
  }

  doConflictCheck() {
    if (this.state.event.hidden_locations > '' || this.state.event.groups > '' || this.state.event.hidden_equipment > '') {
      this.debouncedConflictCheck(this.state.event, !this.facilityConflictHide, !this.groupConflictHide, !this.equipmentConflictHide, this.enableEquipmentQuantities);
    }
  }

  updateEvent(field, value) {
    const updated = {};
    updated[field] = value;

    this.setState(prevState => ({ event: Object.assign({}, prevState.event, updated) }), () => {
      if (field !== 'name' &&
          field !== 'description' &&
          field !== 'virtual_meeting_link' &&
          field !== 'hidden_schools' &&
          field !== 'hidden_groups' &&
          field !== 'override_conflicts' &&
          field !== 'private' &&
          field !== 'user_viewing_groups' &&
          field !== 'hidden_departments' &&
          field !== 'hidden_activities' &&
          field !== 'hidden_event_types') {
        this.doConflictCheck();
      }
    });
  }

  updateSchoolTags() {

      const locationsSelectedItems = this.locationsEventDataGroup.stateProps.selectedItems;
      const groupsSelectedItems = this.groupsEventDataGroup.stateProps.selectedItems;

      var new_hidden_schools = [];

      groupsSelectedItems.forEach((group) => {
        const prefix = group.prefixedId.split('_')[0];
        if (prefix == 's' || prefix == 'ds' || prefix == 'nds') {
          if(new_hidden_schools.indexOf(group.prefixedId) === -1) {
            new_hidden_schools.push(group.prefixedId);
          }
        }
        else if ((prefix === 'gps' || prefix === 'gpc') && group.additional_fields && group.additional_fields.schools) {
          const school_prefix = (prefix === 'gps') ? 'ds' : 'nds';
          group.additional_fields.schools.split(',').forEach((group_school) => {
            var school_prefixedId = `${school_prefix}_${group_school}`;
            if(new_hidden_schools.indexOf(school_prefixedId) === -1) {
              new_hidden_schools.push(school_prefixedId);
            }
          });
        } else if ((prefix === 'us' || prefix === 'u') && group.additional_fields && group.additional_fields.school) {
          const school_prefix = 'ds';
          group.additional_fields.school.split(',').forEach((user_school) => {
            var school_prefixedId = `${school_prefix}_${user_school}`;
            if(new_hidden_schools.indexOf(school_prefixedId) === -1) {
              new_hidden_schools.push(school_prefixedId);
            }
          });
        }
      });

      locationsSelectedItems.forEach((location) => {
        const prefix = location.prefixedId.split('_')[0];
        if (prefix == 's' || prefix == 'ds' || prefix == 'nds') {
          if(new_hidden_schools.indexOf(location.prefixedId) === -1) {
            new_hidden_schools.push(location.prefixedId);
          }
        }
        else if (prefix == 'fl' && location.additional_fields && location.additional_fields.school) {
          const school_prefix = 'ds';
          location.additional_fields.school.split(',').forEach((location_school) => {
            var school_prefixedId = `${school_prefix}_${location_school}`;
            if(new_hidden_schools.indexOf(school_prefixedId) === -1) {
              new_hidden_schools.push(school_prefixedId);
            }
          });
        }
        else if (prefix == 'fr' && location.additional_fields && location.additional_fields.schools) {
          const school_prefix = 'nds';
          location.additional_fields.schools.split(',').forEach((location_school) => {
            var school_prefixedId = `${school_prefix}_${location_school}`;
            if(new_hidden_schools.indexOf(school_prefixedId) === -1) {
              new_hidden_schools.push(school_prefixedId);
            }
          });
        }
      });

      this.updateEvent('hidden_schools', new_hidden_schools.join(','));
  }

  userViewingGroupsChange = (e) => {
    const options = e.target.options;
    const value = [];
    let any_selected = false;
    forEach(options, (option) => {
      if (option.selected) {
        value.push(option.value);
        if(option.value != '') any_selected = true;
      }
    });
    this.updateEvent('user_viewing_groups', value);

    if(this.state.event.config.simple_privacy == '2') this.updateEvent('private', any_selected);
  }

  render() {
    const { event } = this.props;

    if (!event || event.isLoading) {
      return (
        <Loading withWrapper />
      );
    }

    if (event && !event.isLoading && !event.labels) {
      return (
        <div className="centered" style={{ minHeight: 200 }}>
          <Link to="/calendar">View Calendar</Link>
        </div>
      );
    }

    let startDateLabel = 'Event Date';
    if (this.state.event.overnight === true) {
      startDateLabel = 'Start Date';
    }

    const userViewingGroups = this.state.event.user_viewing_groups || [];
    const userViewingGroupsOptions = this.state.event.user_viewing_groups_options || [];

    let eventBubbleOut;

    if(typeof this.state.event.name == 'undefined') this.state.event.name = '';
    if(typeof this.state.event.date_start == 'undefined') this.state.event.date_start = '';
    if(typeof this.state.event.date_end == 'undefined') this.state.event.date_end = '';
    if(typeof this.state.event.time_start == 'undefined') this.state.event.time_start = '';
    if(typeof this.state.event.time_end == 'undefined') this.state.event.time_end = '';
    if(typeof this.state.event.tbd_start == 'undefined') this.state.event.tbd_start = false;
    if(typeof this.state.event.tbd_end == 'undefined') this.state.event.tbd_end = false;
    if(typeof this.state.event.all_day == 'undefined') this.state.event.all_day = false;

    if(this.state.event.date_start == '') this.state.event.date_start = this.state.event.labels.start_moment.format('YYYY-MM-DD');
    if(this.state.event.overnight && this.state.event.date_end == '') this.state.event.date_end = this.state.event.labels.end_moment.format('YYYY-MM-DD');

    if(!this.state.event.all_day)
    {
      if(this.state.event.time_start == '') this.state.event.time_start = '12:00 pm';
      if(this.state.event.time_end == '') this.state.event.time_end = '1:00 pm';
    }

    let uvg_title, uvg_multiple, uvg_size, uvg_value, vug_style;

    uvg_title = 'Viewing Groups';
    uvg_multiple = true;
    uvg_size = 5;
    uvg_value = this.state.event.user_viewing_groups || [];
    vug_style = { width: '100%', marginTop: 10, borderColor: '#d3d3d3' };

    if(this.state.event.config.simple_privacy !== '1') {
      if(this.state.event.config.simple_privacy == '2') {
        uvg_title = 'Visibility';
        uvg_multiple = false;
        uvg_size = 1;
        uvg_value = this.state.event.user_viewing_groups.join(',');
        vug_style.padding = '10px';
      }
    }

    eventBubbleOut = (
      <EventBubble key="event_bubble_object" event={this.state.event} conflicts={this.props.conflictCheck.conflicts} />
    );

    return (
      <section className="event-edit">
        <div className="event-detail-view">
          <OverrideConflicts
            conflicts={this.props.conflictCheck.conflicts}
            event={this.state.event}
            allowOverride={this.showOverrideConflictsCheckbox}
            onConflictOverride={isChecked => this.updateEvent('override_conflicts', isChecked)}
          />

          <CalendarListSection className="inputWithToggle">
            <Input label="Event Name" value={event.name} onChange={e => this.updateEvent('name', e.target.value)} style={{ paddingRight: 119 }} />
            {(this.state.event.config.simple_privacy !== '2') && (
            <UncontrolledSwitch.Toggle
              name="toggle-private"
              value={this.state.event.private}
              onChange={isChecked => this.updateEvent('private', isChecked)}
              style={STYLE.inlineToggle}
              align="right"
            >Private</UncontrolledSwitch.Toggle>
            )}
          </CalendarListSection>

          {((this.state.event.config.simple_privacy == '2' || (this.state.event.config.simple_privacy !== '1' && this.state.event.private)) && userViewingGroupsOptions.length > 0) && (
            <CalendarListSection subtitle={ uvg_title }>
              <select
                id="user_viewing_groups"
                value={ uvg_value }
                onChange={this.userViewingGroupsChange}
                multiple={ uvg_multiple }
                size={ uvg_size }
                style={ vug_style }
                title={ uvg_title }
              >
                {Array(userViewingGroupsOptions.map((q) => {
                  if(typeof q.ghost != 'undefined') {
                    if(q.ghost === 't') {
                      if( typeof this.state.event.user_viewing_groups == 'object' ) {
                        if(this.state.event.user_viewing_groups.indexOf(q.id) === -1) {
                          return false;
                        }
                      } else {
                        return false;
                      }
                    }
                  }
                  return (<option value={q.id} key={`user_viewing_group_option_${q.id}`} dangerouslySetInnerHTML={{ __html: q.data }}></option>)
                }))}
              </select>
            </CalendarListSection>
          )}

          <CalendarListSection subtitle="Event Description">
            {/* <div contentEditable dangerouslySetInnerHTML={{ __html: this.state.event.description }} /> */}
            <Textarea value={this.state.event.description} onChange={e => this.updateEvent('description', e.target.value)} />
          </CalendarListSection>

          <CalendarListSection subtitle={startDateLabel} className="inputWithToggle">
            <DatePicker
              startDatePlaceholderText={startDateLabel}
              date={this.state.event.labels.start_moment}
              onDateChange={this.onStartDateChange}
              disabled={this.state.event.recurrence_id > ''}
            />
            <UncontrolledSwitch.Toggle
              name="toggle-all_day"
              value={event.all_day}
              onChange={isChecked => this.updateEvent('all_day', isChecked)}
              style={STYLE.inlineToggle}
              align="right"
            >All Day</UncontrolledSwitch.Toggle>
          </CalendarListSection>

          {this.state.event.has_setup_breakdown && (
            <CalendarListSection>
              <Input
                type="time"
                label="Setup Time"
                value={EditEventDetails.formatTime(this.state.event.time_setup,
                                                   this.state.event.time_start || '12:00',
                                                   fallback => this.updateEvent('time_setup', fallback))}
                onChange={e => this.updateEvent('time_setup', e.target.value)}
              />
            </CalendarListSection>
          )}

          <CalendarListSection className="inputWithToggle">
            {!this.state.event.all_day && (
              <Input
                type="time"
                label="Start Time"
                value={EditEventDetails.formatTime(this.state.event.time_start, '12:00', fallback => this.updateEvent('time_start', fallback))}
                onChange={e => this.updateEvent('time_start', e.target.value)}
              />
            )}
            {this.state.event.all_day && (
              <Input
                label="Start Time"
                value="All Day"
                disabled
              />
            )}
          </CalendarListSection>

          {this.state.event.overnight && (
            <CalendarListSection subtitle="End Date">
              <DatePicker
                startDatePlaceholderText="End Date"
                date={this.state.event.labels.end_moment}
                onDateChange={this.onEndDateChange}
                isOutsideRange={(day) => {
                  if (moment.isMoment(this.state.event.labels.start_moment)) {
                    return day.isSameOrBefore(this.state.event.labels.start_moment);
                  }
                  return false;
                }}
                disabled={this.state.event.recurrence_id > ''}
              />
            </CalendarListSection>
          )}

          <CalendarListSection className="inputWithToggle">
            {!this.state.event.all_day && (
              <Input
                type="time"
                label="End Time"
                value={EditEventDetails.formatTime(this.state.event.time_end, '13:00', fallback => this.updateEvent('time_end', fallback))}
                onChange={e => this.updateEvent('time_end', e.target.value)}
              />
            )}
            {this.state.event.all_day && (
              <Input
                label="End Time"
                value="All Day"
                disabled
              />
            )}
          </CalendarListSection>

          {this.state.event.has_setup_breakdown && (
            <CalendarListSection>
              <Input
                type="time"
                label="Breakdown Time"
                value={EditEventDetails.formatTime(this.state.event.time_breakdown,
                                                   this.state.event.time_end || '13:00',
                                                   fallback => this.updateEvent('time_breakdown', fallback))}
                onChange={e => this.updateEvent('time_breakdown', e.target.value)}
              />
            </CalendarListSection>
          )}

          <CalendarListSection>
            {this.state.event.overnight && (
              <UncontrolledSwitch.Toggle
                name="toggle-overnight"
                value={event.overnight}
                onChange={isChecked => this.updateEvent('overnight', isChecked)}
                style={STYLE.toggle}
              >Overnight</UncontrolledSwitch.Toggle>
            )}
            <UncontrolledSwitch.Toggle
              name="toggle-setup-breakdown"
              value={this.state.event.has_setup_breakdown}
              onChange={isChecked => this.updateEvent('has_setup_breakdown', isChecked)}
              style={STYLE.toggle}
            >Setup & Breakdown</UncontrolledSwitch.Toggle>
          </CalendarListSection>

          <EventDataGroup
            section="locations"
            subsection={this.searchBrowseSections.locations.subsection_primary}
            config={this.searchBrowseSections.locations}
            defaultSelected={this.state.event.display_locations}
            selectedRender={selected => selected}
            ref={this.setLocationsEventDataGroupRef}
            onChange={(value, selectedItems) => {
              this.updateEvent('hidden_locations', value);
              this.updateSchoolTags();
            }}
          />

          {/* community_group_school */}
          <EventDataGroup
            section="groups"
            subsection={this.searchBrowseSections.groups.subsection_primary}
            config={this.searchBrowseSections.groups}
            defaultSelected={this.state.event.display_groups}
            selectedRender={(selected, item) => {
              const defaultHomeAway = has(item, 'other.home_away_type') ? item.other.home_away_type : 'n';
              return (
                <span>
                  {selected}
                  <select
                    value={this.state.event[`home_away_neutral_${item.prefixedId}`] || defaultHomeAway}
                    onChange={e => this.updateEvent(`home_away_neutral_${item.prefixedId}`, e.target.value)}
                    style={{ width: 80, float: 'right', visibility: 'hidden' }}
                  >
                    <option value="n" key={`home_away_neutral_${item.prefixedId}_n`}>Neutral</option>
                    <option value="h" key={`home_away_neutral_${item.prefixedId}_h`}>Home</option>
                    <option value="a" key={`home_away_neutral_${item.prefixedId}_a`}>Away</option>
                  </select>
                </span>
              );
            }}
            ref={this.setGroupsEventDataGroupRef}
            onChange={(value, selectedItems) => {
              this.updateEvent('hidden_groups', value);
              this.updateEvent('groups', selectedItems.reduce((a, b) => {
                if (b.subsection === 'school_group_restricted' || b.subsection === 'community_group_restricted') {
                  return (a !== '') ? `${a},${b.id}` : `${b.id}`;
                }
                return a;
              }, ''));
              this.updateSchoolTags();
            }}
          />

          <EventDataGroup
            section="equipment"
            subsection={this.searchBrowseSections.equipment.subsection_primary}
            config={this.searchBrowseSections.equipment}
            defaultSelected={this.state.event.display_equipment}
            selectedRender={(selected, item) => {
              if(this.enableEquipmentQuantities) {
                const defaultQuantity = has(item, 'other.quantity_used') ? item.other.quantity_used : 1;
                return (
                    <span>
                      {selected}
                      <select
                        value={this.state.event[`equipment_quantity_${item.id}`] || defaultQuantity}
                        onChange={e => this.updateEvent(`equipment_quantity_${item.id}`, e.target.value)}
                        style={{ width: 65, float: 'right' }}
                      >
                        {Array(parseInt(item.additional_fields.quantity, 10)).fill().map((_, i) => i + 1).map(q => <option value={q} key={q}>{q}</option>)}
                      </select>
                    </span>
                );
              } else {
                return (
                    <span>
                      {selected}
                    </span>
                );
              }
            }}
            onChange={(value) => {
              const eIds = value.replace(/eq_/g, '');
              this.updateEvent('hidden_equipment', eIds);

              if(this.enableEquipmentQuantities) {
                eIds.split(',').forEach((eId) => {
                  if (!this.state.event[`equipment_quantity_${eId}`]) {
                    this.updateEvent(`equipment_quantity_${eId}`, 1);
                  }
                });
              }
            }}
          />

          {this.state.event.config.require_department === '1' && (
            <EventDataGroup
              section="departments"
              subsection="department"
              config={this.searchBrowseSections.departments}
              defaultSelected={this.state.event.display_departments}
              selectedRender={selected => selected}
              onChange={value => this.updateEvent('hidden_departments', value)}
            />
          )}

          {this.state.event.config.require_activity === '1' && (
            <EventDataGroup
              section="activities"
              subsection="activity"
              config={this.searchBrowseSections.activities}
              defaultSelected={this.state.event.display_activities}
              selectedRender={selected => selected}
              onChange={value => this.updateEvent('hidden_activities', value)}
            />
          )}

          {this.state.event.config.require_event_type === '1' && (
            <EventDataGroup
              section="event_types"
              subsection="event_type"
              config={this.searchBrowseSections.event_types}
              defaultSelected={this.state.event.display_event_types}
              selectedRender={selected => selected}
              onChange={value => this.updateEvent('hidden_event_types', value)}
            />
          )}

          {this.enableEventVirtualMeetingLink && (
            <CalendarListSection>
              <Input
                type="text"
                label="Virtual Meeting Link"
                value={this.state.event.virtual_meeting_link}
                onChange={e => this.updateEvent('virtual_meeting_link', e.target.value)}
              />
            </CalendarListSection>
          )}

          {eventBubbleOut}

          {this.getSubmitButton()}
        </div>
      </section>
    );
  }
}

function mapStateToProps(state, ownProps) {
  const { eventConflictChecks } = state;
  let conflictCheck = {};
  if (has(ownProps, 'event.id') && ownProps.event.id) {
    conflictCheck = eventConflictChecks.events[ownProps.event.id] || { isLoading: false, conflicts: [] };
  } else {
    conflictCheck = eventConflictChecks.addEvent || { isLoading: false, conflicts: [] };
  }

  return {
    conflictCheck,
  };
}

export default connect(mapStateToProps, {
  updateEvent,
  addEvent,
  checkEventConflicts,
  setFlashMessage,
  removeFlashMessage,
  dataGroupResetAll,
  push,
})(EditEventDetails);



// WEBPACK FOOTER //
// ./src/app/components/calendar/EditEventDetails.js